import { useEffect, useState } from "react";
import Topbar from "../../components/Topbar";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Store, Pencil, Ban, X, Save } from "lucide-react";

const EMPTY_FORM = {
  name: "", city: "", state: "", address: "", pos_branch_code: "",
  wansoft_subsidiary_id: "", wansoft_warehouse_id: "", wansoft_name: "",
};

function BranchModal({ branch, onClose, onSaved }) {
  const isEdit = !!branch;
  const [form, setForm] = useState(branch ? {
    name: branch.name || "", city: branch.city || "", state: branch.state || "",
    address: branch.address || "", pos_branch_code: branch.pos_branch_code || "",
    wansoft_subsidiary_id: branch.wansoft_subsidiary_id ?? "",
    wansoft_warehouse_id: branch.wansoft_warehouse_id ?? "",
    wansoft_name: branch.wansoft_name || "",
  } : EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        city: form.city.trim(),
        state: form.state.trim(),
        address: form.address.trim(),
        pos_branch_code: form.pos_branch_code.trim() || null,
        wansoft_subsidiary_id: form.wansoft_subsidiary_id === "" ? null : parseInt(form.wansoft_subsidiary_id, 10),
        wansoft_warehouse_id: form.wansoft_warehouse_id === "" ? null : parseInt(form.wansoft_warehouse_id, 10),
        wansoft_name: form.wansoft_name.trim() || null,
      };
      if (isEdit) {
        await api.put(`/branches/${branch.id}`, payload);
        toast.success("Sucursal actualizada");
      } else {
        await api.post("/branches", payload);
        toast.success("Sucursal creada. Ya está disponible en todos los selectores/dashboards.");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar la sucursal");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="branch-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-lg">{isEdit ? "Editar sucursal" : "Nueva sucursal"}</h3>
          <button onClick={onClose} data-testid="branch-modal-close-btn"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-500">Nombre *</label>
            <input value={form.name} onChange={e => set("name", e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="branch-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-500">Ciudad</label>
              <input value={form.city} onChange={e => set("city", e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="branch-form-city" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500">Estado</label>
              <input value={form.state} onChange={e => set("state", e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="branch-form-state" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Dirección</label>
            <input value={form.address} onChange={e => set("address", e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="branch-form-address" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-500">Código POS (opcional, ej. HMO-01)</label>
            <input value={form.pos_branch_code} onChange={e => set("pos_branch_code", e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm font-mono" data-testid="branch-form-pos-code" />
          </div>

          <div className="pt-2 border-t border-slate-100">
            <div className="text-xs font-semibold text-slate-600 mb-2">Mapeo Wansoft (opcional — completar cuando el cliente dé de alta la sucursal en Wansoft)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-500">Subsidiary ID</label>
                <input type="number" value={form.wansoft_subsidiary_id} onChange={e => set("wansoft_subsidiary_id", e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="branch-form-wansoft-subsidiary" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Warehouse ID</label>
                <input type="number" value={form.wansoft_warehouse_id} onChange={e => set("wansoft_warehouse_id", e.target.value)}
                  className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="branch-form-wansoft-warehouse" />
              </div>
            </div>
            <div className="mt-3">
              <label className="text-xs font-semibold text-slate-500">Nombre en Wansoft</label>
              <input value={form.wansoft_name} onChange={e => set("wansoft_name", e.target.value)}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" data-testid="branch-form-wansoft-name" />
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Sin estos datos, la sucursal simplemente se omite del sync de inventario hasta que se completen — no requiere tocar código.</p>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 rounded-xl text-sm font-semibold border border-slate-300">Cancelar</button>
          <button onClick={submit} disabled={saving} data-testid="branch-form-submit-btn"
            className="btn-primary-chizy px-4 py-2 rounded-xl text-sm inline-flex items-center gap-2 disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Guardando..." : isEdit ? "Guardar cambios" : "Crear sucursal"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Sucursales() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/branches");
      setBranches((r.data || []).filter(b => !b.is_corporate));
    } catch (e) {
      toast.error("Error al cargar sucursales");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const deactivate = async (branch) => {
    if (!window.confirm(`¿Desactivar "${branch.name}"? Deja de aparecer en selectores y dashboards, pero se conserva su historial.`)) return;
    try {
      await api.delete(`/branches/${branch.id}`);
      toast.success("Sucursal desactivada");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al desactivar");
    }
  };

  return (
    <>
      <Topbar title="Sucursales" subtitle="Alta, edición y desactivación de sucursales" />
      <div className="p-6 pb-28 space-y-5 max-w-4xl" data-testid="sucursales-page">
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">Sumar una sucursal nueva no requiere deploy — aparece automáticamente en dashboards, reportes y selectores.</p>
          <button onClick={() => { setEditing(null); setModalOpen(true); }} data-testid="sucursal-add-btn"
            className="btn-primary-chizy px-4 py-2.5 rounded-xl text-sm inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nueva sucursal
          </button>
        </div>

        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="bg-white rounded-xl border border-slate-200 h-20 animate-pulse" />)}</div>
        ) : (
          <div className="space-y-3" data-testid="sucursales-list">
            {branches.map(b => (
              <div key={b.id} className={`bg-white rounded-xl border p-4 flex items-center justify-between gap-4 ${b.active === false ? "border-slate-200 opacity-60" : "border-slate-200"}`}
                data-testid={`sucursal-row-${b.id}`}>
                <div className="flex items-center gap-3 flex-1">
                  <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center shrink-0">
                    <Store className="w-5 h-5 text-yellow-700" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 text-sm flex items-center gap-2">
                      {b.name}
                      {b.active === false && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Inactiva</span>}
                    </div>
                    <div className="text-xs text-slate-500">
                      {[b.city, b.state].filter(Boolean).join(", ") || "Sin ciudad/estado"}
                      {b.wansoft_subsidiary_id ? " · Wansoft OK" : " · Sin mapeo Wansoft"}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => { setEditing(b); setModalOpen(true); }} data-testid={`sucursal-edit-btn-${b.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-slate-300 hover:bg-slate-50">
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </button>
                  {b.active !== false && (
                    <button onClick={() => deactivate(b)} data-testid={`sucursal-deactivate-btn-${b.id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-300 text-red-700 hover:bg-red-50">
                      <Ban className="w-3.5 h-3.5" /> Desactivar
                    </button>
                  )}
                </div>
              </div>
            ))}
            {branches.length === 0 && <div className="text-sm text-slate-400 p-6 text-center">No hay sucursales todavía</div>}
          </div>
        )}
      </div>
      {modalOpen && (
        <BranchModal branch={editing} onClose={() => setModalOpen(false)} onSaved={() => { setModalOpen(false); load(); }} />
      )}
    </>
  );
}
